import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Message } from '@/interfaces/message';
import { useDemoCruiseStore } from './demoCruiseStore';

export const useDemoMessageStore = defineStore('demoMessage', () => {
  // Sample messages for demo purposes
  const messages = ref<Record<string, Message[]>>({
    // Cruise 1 messages
    'cruise-1': [
      {
        id: 1,
        content: 'Welcome to the Caribbean Adventure cruise!',
        senderInfo: 'System',
        timestamp: '2023-06-01T10:00:00Z',
        read: true,
        duckUuid: '',
        source: 'SYSTEM'
      },
      {
        id: 2,
        content: 'All ducks have been deployed for the adventure.',
        senderInfo: 'System',
        timestamp: '2023-06-01T11:30:00Z',
        read: false,
        duckUuid: '',
        source: 'SYSTEM'
      },
      {
        id: 3,
        content: 'A passenger has reported a duck sighting near the pool.',
        senderInfo: 'Admin',
        timestamp: '2023-06-02T09:15:00Z',
        read: false,
        duckUuid: '',
        source: 'USER'
      }
    ],
    
    // Duck 101 messages
    'duck-uuid-101': [
      {
        id: 101,
        content: 'I found this duck by the pool!',
        senderInfo: 'Passenger',
        timestamp: '2023-06-02T14:20:00Z',
        read: false,
        duckUuid: 'duck-uuid-101',
        duckId: 101,
        source: 'PUBLIC'
      },
      {
        id: 102,
        content: 'Thanks for finding my duck! Enjoy your prize!',
        senderInfo: 'Admin',
        timestamp: '2023-06-02T16:45:00Z',
        read: false,
        duckUuid: 'duck-uuid-101',
        duckId: 101,
        source: 'USER'
      }
    ]
  });

  // Get messages for a cruise
  const getCruiseMessages = (cruiseId: number) => {
    const cruiseStore = useDemoCruiseStore();
    
    // Get the cruise by ID
    const cruise = cruiseStore.getCruiseById(cruiseId);
    if (!cruise) return [];
    
    // Get all duck UUIDs for this cruise
    const duckUuids = cruise.ducks.map(duck => duck.uuid);
    
    // Collect all messages from the ducks in this cruise
    let cruiseMessages: Message[] = [];
    duckUuids.forEach(uuid => {
      if (messages.value[uuid]) {
        cruiseMessages = cruiseMessages.concat(messages.value[uuid]);
      }
    });
    
    // Also include cruise-specific messages if they exist
    const cruiseKey = `cruise-${cruiseId}`;
    if (messages.value[cruiseKey]) {
      cruiseMessages = cruiseMessages.concat(messages.value[cruiseKey]);
    }
    
    // Sort messages by timestamp (newest first)
    return cruiseMessages.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  };

  // Get messages for a duck
  const getDuckMessages = (duckUuid: string) => {
    return messages.value[duckUuid] || [];
  };

  // Add a message to a cruise
  const addCruiseMessage = (cruiseId: number, content: string, source: 'USER' | 'SYSTEM' = 'USER') => {
    const key = `cruise-${cruiseId}`;
    if (!messages.value[key]) {
      messages.value[key] = [];
    }

    const newId = messages.value[key].length > 0 
      ? Math.max(...messages.value[key].map(m => m.id)) + 1 
      : 1;

    const newMessage: Message = {
      id: newId,
      content,
      senderInfo: source === 'USER' ? 'Admin' : 'System',
      timestamp: new Date().toISOString(),
      read: false,
      duckUuid: '',
      source
    };

    messages.value[key].push(newMessage);

    // Update unread count in cruise store
    const cruiseStore = useDemoCruiseStore();
    const cruise = cruiseStore.getCruiseById(cruiseId);
    if (cruise) {
      cruise.unreadMessageCount = (cruise.unreadMessageCount || 0) + 1;
    }

    return newMessage;
  };

  // Add a message to a duck
  const addDuckMessage = (duckUuid: string, content: string, source: 'USER' | 'PUBLIC' = 'PUBLIC') => {
    if (!messages.value[duckUuid]) {
      messages.value[duckUuid] = [];
    }

    const newId = messages.value[duckUuid].length > 0 
      ? Math.max(...messages.value[duckUuid].map(m => m.id)) + 1 
      : 1;

    const cruiseStore = useDemoCruiseStore();
    const duckInfo = cruiseStore.getDuckByUuid(duckUuid);
    const duckId = duckInfo?.duck.id ? parseInt(String(duckInfo.duck.id), 10) : undefined;

    const newMessage: Message = {
      id: newId,
      content,
      senderInfo: source === 'USER' ? 'Admin' : 'Passenger',
      timestamp: new Date().toISOString(),
      read: false,
      duckUuid,
      duckId,
      source
    };

    messages.value[duckUuid].push(newMessage);

    // Update unread count in duck and cruise
    if (duckInfo) {
      duckInfo.duck.unreadMessageCount = (duckInfo.duck.unreadMessageCount || 0) + 1;
      
      const cruise = cruiseStore.getCruiseById(duckInfo.cruiseId);
      if (cruise) {
        cruise.unreadMessageCount = (cruise.unreadMessageCount || 0) + 1;
      }
    }

    return newMessage;
  };

  // Mark messages as read
  const markAsRead = (key: string) => {
    if (messages.value[key]) {
      messages.value[key].forEach(message => {
        message.read = true;
      });

      // Update unread counts in cruise store
      const cruiseStore = useDemoCruiseStore();
      
      // If it's a cruise key
      if (key.startsWith('cruise-')) {
        const cruiseId = parseInt(key.split('-')[1]);
        const cruise = cruiseStore.getCruiseById(cruiseId);
        if (cruise) {
          cruise.unreadMessageCount = 0;
        }
      } 
      // If it's a duck key
      else {
        const duckInfo = cruiseStore.getDuckByUuid(key);
        if (duckInfo) {
          duckInfo.duck.unreadMessageCount = 0;
        }
      }
    }
  };

  return {
    messages,
    getCruiseMessages,
    getDuckMessages,
    addCruiseMessage,
    addDuckMessage,
    markAsRead
  };
});